import { remapCopiedGroups } from "./groups";
import type { SceneNode } from "../scene/model";
import { selectable } from "./geometry";

export type LayerOrder = "forward" | "backward" | "front" | "back";

export function assertNativeLayer(node: SceneNode) {
  if (
    ["groupId", "groupName"].some(
      (k) =>
        node.layer[k] !== undefined &&
        (typeof node.layer[k] !== "string" ||
          String(node.layer[k]).length > 1000),
    ) ||
    ["clipToBelow", "groupCollapsed"].some(
      (k) => node.layer[k] !== undefined && typeof node.layer[k] !== "boolean",
    ) ||
    (node.layer.clipToBelow === true && node.kind !== "image") ||
    (node.layer.name !== undefined && typeof node.layer.name !== "string") ||
    (node.layer.locked !== undefined && typeof node.layer.locked !== "boolean")
  )
    throw new Error("Повреждены имя или состояние блокировки слоя.");
}

export function renameLayer(node: SceneNode, name: string): SceneNode {
  const value = name.trim();
  if (!value || value === node.layer.name) return node;
  return { ...node, layer: { ...node.layer, name: value } };
}

export function setLayerFlag(
  node: SceneNode,
  flag: "visible" | "locked",
  value: boolean,
): SceneNode {
  if (flag === "visible") {
    if ((node.appearance.visible !== false) === value) return node;
    return { ...node, appearance: { ...node.appearance, visible: value } };
  }
  if ((node.layer.locked === true) === value) return node;
  return { ...node, layer: { ...node.layer, locked: value } };
}

/** The document order is bottom-to-top; the panel order is its reverse. */
export function moveLayerBefore(
  nodes: readonly SceneNode[],
  id: string,
  beforeId: string | null,
): SceneNode[] {
  return moveLayersBefore(nodes, [id], beforeId);
}

/** Move a selection as one stack, in document order, without cloning its nodes. */
export function moveLayersBefore(
  nodes: readonly SceneNode[],
  ids: readonly string[],
  beforeId: string | null,
): SceneNode[] {
  const requested = new Set(ids),
    moving = nodes.filter((n) => requested.has(n.id) && !n.layer.locked),
    selected = new Set(moving.map((n) => n.id));
  if (
    !moving.length ||
    (beforeId !== null &&
      (selected.has(beforeId) || !nodes.some((n) => n.id === beforeId)))
  )
    return nodes.slice();
  const rest = nodes.filter((n) => !selected.has(n.id));
  const index =
    beforeId === null ? 0 : rest.findIndex((n) => n.id === beforeId) + 1;
  rest.splice(index, 0, ...moving);
  return rest;
}

/** Move selected runs one level, or the entire selection to an edge, without changing their relative order. */
export function orderLayers(
  nodes: readonly SceneNode[],
  ids: readonly string[],
  direction: LayerOrder,
): SceneNode[] {
  const requested = new Set(ids);
  const selected = new Set(
    nodes
      .filter((n) => requested.has(n.id) && !n.layer.locked)
      .map((n) => n.id),
  );
  const moving = (node: SceneNode) => selected.has(node.id);
  if (direction === "front")
    return [...nodes.filter((n) => !moving(n)), ...nodes.filter(moving)];
  if (direction === "back")
    return [...nodes.filter(moving), ...nodes.filter((n) => !moving(n))];
  const result = nodes.slice();
  if (direction === "forward") {
    for (let i = result.length - 2; i >= 0; i--)
      if (moving(result[i]!) && !moving(result[i + 1]!))
        [result[i], result[i + 1]] = [result[i + 1]!, result[i]!];
  } else {
    for (let i = 1; i < result.length; i++)
      if (moving(result[i]!) && !moving(result[i - 1]!))
        [result[i - 1], result[i]] = [result[i]!, result[i - 1]!];
  }
  return result;
}

/** Copies own IDs and values, but continue referring to the original immutable assets. */
export function duplicateLayers(
  nodes: readonly SceneNode[],
  ids: readonly string[],
  newId: () => string,
) {
  const requested = new Set(ids),
    used = new Set(nodes.map((n) => n.id));
  const copies = nodes
    .filter((n) => requested.has(n.id) && selectable(n))
    .map((node) => {
      const id = newId();
      if (!id || used.has(id))
        throw new Error("Не удалось создать уникальный номер слоя.");
      used.add(id);
      const copy = structuredClone(node);
      copy.id = id;
      if (copy.interaction.objectId) copy.interaction.objectId = id;
      copy.transform.x = node.transform.x! + 20;
      copy.transform.y = node.transform.y! + 20;
      if (
        ![copy.transform.x, copy.transform.y].every(
          (n) => Number.isFinite(n) && Math.abs(n) <= 1e6,
        )
      )
        throw new Error("Копия выйдет за допустимые координаты документа.");
      return copy;
    });
  remapCopiedGroups(copies, nodes, ids, newId);
  return { nodes: [...nodes, ...copies], ids: copies.map((n) => n.id) };
}
